import Icon from '@/components/ui/AppIcon';

interface Resource {
  id: number;
  icon: string;
  title: string;
  description: string;
  link: string;
  badge?: string;
}

const resources: Resource[] = [
  {
    id: 1,
    icon: "BookOpenIcon",
    title: "Content Creation Guide",
    description: "Learn the fundamentals of creating engaging audio content that resonates with your audience",
    link: "#",
    badge: "Popular"
  },
  {
    id: 2,
    icon: "MicrophoneIcon",
    title: "Recording Best Practices",
    description: "Professional tips on audio quality, equipment setup, and recording techniques",
    link: "#"
  },
  {
    id: 3,
    icon: "ChartBarIcon",
    title: "Growth Strategies",
    description: "Proven methods to grow your audience and increase engagement on the platform",
    link: "#",
    badge: "New"
  },
  {
    id: 4,
    icon: "CurrencyRupeeIcon",
    title: "Monetization Masterclass",
    description: "Maximize your earnings with advanced monetization strategies and pricing tips",
    link: "#"
  },
  {
    id: 5,
    icon: "UserGroupIcon",
    title: "Community Building",
    description: "Build a loyal fanbase through effective engagement and community management",
    link: "#"
  },
  {
    id: 6,
    icon: "MegaphoneIcon",
    title: "Marketing Your Content",
    description: "Social media strategies and promotional techniques to reach wider audiences",
    link: "#"
  }
];

interface SupportOption {
  id: number;
  icon: string;
  title: string;
  description: string;
  action: string;
}

const supportOptions: SupportOption[] = [
  {
    id: 1,
    icon: "ChatBubbleLeftRightIcon",
    title: "Creator Support",
    description: "Get help from our dedicated creator support team",
    action: "Contact Support"
  },
  {
    id: 2,
    icon: "UserGroupIcon",
    title: "Creator Community",
    description: "Connect with fellow creators in our private forum",
    action: "Join Community"
  },
  {
    id: 3,
    icon: "VideoCameraIcon",
    title: "Live Workshops",
    description: "Attend weekly live sessions with industry experts",
    action: "View Schedule"
  }
];

export default function ResourcesSection() {
  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-accent/10 rounded-full mb-4">
            <Icon name="AcademicCapIcon" size={20} className="text-accent" />
            <span className="text-sm font-semibold text-accent">Creator Resources</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Learn, Grow, and Excel
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Access free educational resources to help you become a successful content creator
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="group bg-card rounded-xl p-6 shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
            >
              {resource.badge && (
                <div className="inline-block px-3 py-1 bg-secondary text-secondary-foreground rounded-full text-xs font-bold mb-4">
                  {resource.badge}
                </div>
              )}
              
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                <Icon name={resource.icon as any} size={24} className="text-primary group-hover:text-primary-foreground" />
              </div>

              <h3 className="text-lg font-heading font-bold text-text-primary mb-2 group-hover:text-primary transition-colors duration-300">
                {resource.title}
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed mb-4">
                {resource.description}
              </p>

              <div className="flex items-center space-x-2 text-primary font-semibold text-sm group-hover:translate-x-1 transition-transform duration-300">
                <span>Learn More</span>
                <Icon name="ArrowRightIcon" size={16} />
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 mb-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
              Need Help? We're Here for You
            </h3>
            <p className="text-text-secondary">
              Multiple support channels to ensure your success on the platform
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {supportOptions.map((option) => (
              <div
                key={option.id}
                className="bg-card rounded-xl p-6 text-center shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={option.icon as any} size={28} className="text-primary" />
                </div>
                <h4 className="text-lg font-heading font-bold text-text-primary mb-2">
                  {option.title}
                </h4>
                <p className="text-text-secondary text-sm mb-4">
                  {option.description}
                </p>
                <button className="w-full px-4 py-2 bg-primary text-primary-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-primary-dark">
                  {option.action}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-2xl p-8 shadow-warm text-center">
          <Icon name="EnvelopeIcon" size={48} className="text-primary mx-auto mb-4" />
          <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
            Stay Updated with Creator Newsletter
          </h3>
          <p className="text-text-secondary mb-6 max-w-xl mx-auto">
            Get weekly tips, platform updates, and exclusive opportunities delivered to your inbox
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              className="flex-1 px-4 py-3 bg-input border-2 border-border rounded-lg focus:outline-none focus:border-primary transition-colors duration-300"
            />
            <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-primary-dark hover:shadow-warm whitespace-nowrap">
              Subscribe Now
            </button>
          </div>

          <p className="text-xs text-text-secondary mt-4">
            Join 5,000+ creators receiving our weekly newsletter
          </p>
        </div>
      </div>
    </section>
  );
}